/**
 * Customer Leaderboard Chart Component
 *
 * Horizontal bar chart displaying top customers ranked by revenue.
 * Shows masked customer emails and formatted revenue amounts.
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import type { CustomerLeaderboardItem } from '../../../types/financialInsights';
import {
  formatChartCurrency,
  truncateLabel,
  CHART_COLORS,
} from '../../../utils/chartHelpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface CustomerLeaderboardChartProps {
  data: CustomerLeaderboardItem[] | undefined;
  loading: boolean;
}

/**
 * Loading skeleton for chart
 */
const ChartSkeleton: React.FC = () => (
  <Box sx={{ height: 400 }}>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Skeleton variant="rectangular" width={200} height={32} />
    </Box>
    <Skeleton variant="rectangular" width="100%" height={340} />
  </Box>
);

/**
 * Customer Leaderboard Chart
 */
export const CustomerLeaderboardChart: React.FC<
  CustomerLeaderboardChartProps
> = ({ data, loading }) => {
  const theme = useTheme();

  // Show skeleton during loading
  if (loading) {
    return (
      <Card>
        <CardContent>
          <ChartSkeleton />
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{ height: 400 }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Customer Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No customer revenue data found for this period
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = {
    labels: data.map(customer => truncateLabel(customer.customer_name, 30)),
    datasets: [
      {
        label: 'Revenue',
        data: data.map(customer => customer.total_revenue),
        backgroundColor: CHART_COLORS[0],
        borderColor: CHART_COLORS[0],
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 4,
        callbacks: {
          label: (context: any) => {
            const customer = data[context.dataIndex];
            return [
              `Revenue: ${formatChartCurrency(customer.total_revenue)}`,
              `Transactions: ${customer.transaction_count}`,
              `Avg Transaction: ${formatChartCurrency(customer.average_transaction_size)}`,
              customer.customer_email
                ? `Email: ${customer.customer_email}`
                : '',
            ].filter(line => line !== '');
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: 'Arial, sans-serif',
            size: 11,
          },
          callback: (value: any) => {
            const numValue = Number(value);
            if (Number.isNaN(numValue)) return '';
            return formatChartCurrency(numValue, 'USD', true);
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            family: 'Arial, sans-serif',
            size: 11,
          },
        },
      },
    },
  };

  // Calculate totals
  const totalRevenue = data.reduce(
    (sum, customer) => sum + (customer.total_revenue || 0),
    0
  );
  const totalTransactions = data.reduce(
    (sum, customer) => sum + (customer.transaction_count || 0),
    0
  );

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Top Customers by Revenue
          </Typography>
        </Box>

        {/* Chart */}
        <Box sx={{ height: 340, position: 'relative' }}>
          <Bar data={chartData} options={chartOptions} />
        </Box>

        {/* Summary statistics */}
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="caption" color="text.secondary">
            Showing top {data.length} customer{data.length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total: {formatChartCurrency(totalRevenue)} ({totalTransactions}{' '}
            transactions)
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CustomerLeaderboardChart;
